import dayjs from 'dayjs';
import { ExecuteUberSearchInput } from 'Generated/schema';
import { FilterKeyType } from 'Pages/Investigate';
import { ColumnProperty, Filter, Filters, UnifiedFormValues } from '../types';
import { transformUnifiedFormValuesToSimpleSearchValues } from '../CrossLogSearch/helpers/utils';

// Drops length-0 strings from string-array values. The IN-list editor
// (TagsValueInputV3) is fully controlled and emits an empty trailing slot
// while the user is mid-typing — those must never reach the backend, where
// IN ('') would silently match every empty field (ASK-3863 / EPD-2815).
const stripEmptyArrayElements = (value: Filter['value']): Filter['value'] => {
  if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
    return (value as string[]).filter(s => s.length > 0);
  }
  return value;
};

export const createFormattedFilter = ({ key, keyType, operator, value }: Filter) => {
  const valueToSubmit =
    operator?.type === null ? operator?.initialFieldValue : stripEmptyArrayElements(value);
  let filterObj = {
    key: keyType === FilterKeyType.Object ? (key as ColumnProperty)?.path : key,
    operator: operator?.value,
    value: valueToSubmit,
  } as unknown as Filter;

  // When an operator has a null type AND the value is null, we don't want
  // to add the value field to the submitted filter row at all.
  if (operator?.type === null && valueToSubmit === null) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { value: removedValue, ...cleanedFilterObj } = filterObj;
    filterObj = cleanedFilterObj;
  }

  return filterObj;
};

const createFormattedFilters = (
  filtersRow: Filter[],
  shouldFilterOutIncompleteFilterRows?: boolean,
): Filter[] => {
  const filteredFiltersRow = shouldFilterOutIncompleteFilterRows
    ? filtersRow.filter(f => {
        // We only want to submit the filter row if it has a key and operator.
        return f.key && f.operator;
      })
    : filtersRow;
  const formattedFilters = filteredFiltersRow.map((filter: Filter) =>
    createFormattedFilter(filter),
  );

  return formattedFilters;
};

export const getCompleteFilterConditions = (
  filterConditions: Array<{
    uuid: string;
    ands: Filter[];
  }>,
) => {
  return filterConditions.filter(filterRow => filterRow.ands.length > 0);
};

export const createFiltersString = (
  filters: Filters,
  shouldFilterOutIncompleteFilterRows?: boolean,
): string => {
  // Filter out empty rows
  const filteredConditions = shouldFilterOutIncompleteFilterRows
    ? getCompleteFilterConditions(filters.ors)
    : filters.ors;

  const formattedFiltersObject = {
    ors: filteredConditions.map(filterRowArray => {
      return {
        ands: createFormattedFilters(filterRowArray.ands, shouldFilterOutIncompleteFilterRows),
      };
    }),
  };

  return JSON.stringify(formattedFiltersObject);
};

export const hasFilters = filters => {
  return filters.ors.some(filterCondition => filterCondition.ands.length > 0);
};

export const createSimpleSearchPayload = (
  values: UnifiedFormValues,
  shouldFilterOutIncompleteFilterRows = true,
) => {
  const { startTime, endTime, database, table, filters, limit, sortDirection, language } =
    transformUnifiedFormValuesToSimpleSearchValues(values);

  const payload: ExecuteUberSearchInput = {
    params: {
      databases: [database],
      tables: [table],
      startTime: dayjs.utc(startTime).toISOString(),
      endTime: dayjs.utc(endTime).toISOString(),
      limit: Number(limit),
      sortDir: sortDirection,
      language,
    },
  };

  // We only add the filters field to the payload if there are filters.
  if (hasFilters(filters)) {
    payload.params.filters = createFiltersString(filters, shouldFilterOutIncompleteFilterRows);
  }

  return payload;
};
